import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.closeDetails = this.closeDetails.bind(this)
    this.onHistoryChange = this.onHistoryChange.bind(this)
    this.closeMobileFilters = this.closeMobileFilters.bind(this)
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500).bind(this)

    const facetForm = this.querySelector('form')
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this))
  }

  connectedCallback() {
    this.submitInputs = this.querySelectorAll('.js-filter-submit')
    this.sortInput = this.querySelector('.js-sort-input')
    this.closeButton = this.querySelector('.js-close')
    this.drawer = document.querySelector('#collection-filters')

    this.bindActiveFacetButtonEvents()
    window.addEventListener('popstate', this.onHistoryChange)
    this.drawer?.addEventListener('drawer:close', this.closeDetails)
    this.submitInputs?.forEach((element) => {
      element.addEventListener('click', this.debouncedOnSubmit)
    })
    this.sortInput?.addEventListener('change', this.debouncedOnSubmit)
    this.closeButton?.addEventListener('click', this.closeMobileFilters)
  }

  disconnectedCallback() {
    this.submitInputs = this.querySelectorAll('.js-filter-submit')
    this.sortInput = this.querySelector('.js-sort-input')
    this.closeButton = this.querySelector('.js-close')

    window.removeEventListener('popstate', this.onHistoryChange)
    this.drawer?.removeEventListener('drawer:close', this.closeDetails)
    this.submitInputs?.forEach((element) => {
      element.removeEventListener('click', this.debouncedOnSubmit)
    })
    this.sortInput?.removeEventListener('change', this.debouncedOnSubmit)
    this.closeButton?.removeEventListener('click', this.closeMobileFilters)
  }

  closeMobileFilters() {
    this.dispatchEvent(
      new CustomEvent('closeModal', {
        bubbles: true,
      })
    )
  }

  closeDetails(event) {
    const detailsElements = this.querySelectorAll('.js-filter')
    detailsElements.forEach((detailsElement) => {
      if (!detailsElement.contains(event.target)) {
        detailsElement.open = false
      }
    })
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const formData = new FormData(event.target.closest('form'))
    const searchParams = new URLSearchParams(formData).toString()
    this.renderPage(searchParams, event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable)
    })
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document
      .getElementById('CollectionProductGrid')
      .querySelector('collection-product-grid')
      .classList.add('is-loading')

    const url =
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${searchParams}`

    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event)

    if (updateURLHash) this.updateURLHash(searchParams)
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderFilters(html, event)
        this.renderProductGrid(html)
      })
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderFilters(html, event)
    this.renderProductGrid(html)
  }

  renderProductGrid(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('CollectionProductGrid').innerHTML

    document.getElementById('CollectionProductGrid').innerHTML = innerHTML
  }

  renderFilters(...args) {
    this.renderSingleFormFilters('#CollectionFiltersForm', ...args)
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    this.renderActiveFacets(parsedHTML)

    const facetDetailsElements = parsedHTML.querySelectorAll(
      `${form} .js-filter`
    )
    const matchesIndex = (element) =>
      element.dataset.index ===
      event?.target.closest('.js-filter')?.dataset.index

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    )

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex)

    facetsToRender.forEach((element) => {
      document.querySelector(
        `${form} .js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML
    })

    this.renderAdditionalElements(parsedHTML)

    if (countsToRender) {
      this.renderCounts(countsToRender, event.target.closest('.js-filter'))
    }
  }

  renderActiveFacets(html) {
    document
      .querySelectorAll('.filter__summary-active-filters')
      .forEach((selector) => {
        const activeFacetsElement = html.querySelector(
          `[data-related-filter="${selector.dataset.relatedFilter}"]`
        )
        if (!activeFacetsElement) return
        selector.innerHTML = activeFacetsElement.innerHTML
        selector.classList.toggle('hidden', !activeFacetsElement.innerHTML)
      })

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
    this.toggleClearAllButton(html)
  }

  renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '[data-total-products]',
      '.collection-filters-trigger',
    ]

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML
    })
  }

  renderCounts(source, target) {
    const countElementSelectors = ['.js-bubble', '.js-selected']
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  toggleClearAllButton(html) {
    const facetCount =
      html.querySelectorAll('.collection-filters__facet.js-facet-remove')
        ?.length || 0

    document.querySelectorAll('.js-clear-all').forEach(element => {
      if (facetCount > 0) {
        element.classList.remove('hidden')
      } else {
        element.classList.add('hidden')
      }
    })

    document.querySelectorAll('.js-filter-apply').forEach((element) => {
      if (facetCount > 0) {
        element.removeAttribute('disabled')
      } else {
        element.setAttribute('disabled', true)
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters').dataset.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset
          .id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)
